import React, { useState, useEffect } from "react";
import { sendEmailWithPdf } from "../components/sendEmailWithPdf";
import { alertC } from '../components/dialogService';
import { authFetch } from '../utils/api';

const EmailDialog = ({
  simplePdf = true,
  isOpen,
  onClose,
  isSending,
  setIsSending,
  container = ".view-details",
  filename = "Quote",
  subject = null,   // null = load from DB
  message = null,   // null = load from DB
  customerEmail = null
}) => {
  const [recipient, setRecipient] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [overrideSubject, setOverrideSubject] = useState("");
  const [overrideMessage, setOverrideMessage] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [scale, setScale] = useState("2");
  const [emailSubject, setEmailSubject] = useState(subject || "");
  const [emailMessage, setEmailMessage] = useState(message || "");

  let pdfFixStyle = null;

  useEffect(() => {
    setRecipient(customerEmail);
  }, []);

  // Load default subject/message from mailer settings if not provided as props
  useEffect(() => {
    if (!isOpen) return;
    if (subject && message) return;
    authFetch('/email-settings/')
      .then(data => {
        const map = {};
        (data || []).forEach(s => { map[s.key] = s.value; });
        if (!subject) setEmailSubject(map.default_subject || "Your Quote");
        if (!message) setEmailMessage(map.default_message || "Please find your quote attached.");
      })
      .catch(() => {
        if (!subject) setEmailSubject("Your Quote");
        if (!message) setEmailMessage("Please find your quote attached.");
      });
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!recipient || !recipient.includes("@")) {
      alertC("Please enter a valid email address.");
      return;
    }

    const numericScale = parseFloat(scale);
    if (isNaN(numericScale) || numericScale < 1 || numericScale > 5) {
      alertC("Scale must be a number between 1.0 and 5.0");
      return;
    }

    setIsSending(true);

    pdfFixStyle = document.createElement('style');
    pdfFixStyle.textContent = `
      ${container} s,
      ${container} del,
      ${container} [style*="text-decoration: line-through"],
      ${container} .line-through {
        text-decoration: none !important;
        background-image: linear-gradient(transparent 45%, currentColor 45%, currentColor 55%, transparent 55%) !important;
        background-repeat: repeat-x !important;
        background-size: 1px 1em !important;
        background-position: 0 0.62em !important;
      }
    `;
    document.head.appendChild(pdfFixStyle);

    document.querySelectorAll('.specs-master-control').forEach(el => {
      el.dataset.originalDisplay = el.style.display || '';
      el.style.display = 'none';
    });

    try {
      const response = await sendEmailWithPdf({
        simplePdf,
        recipient,
        recipientName: recipientName.trim(),
        container,
        filename,
        subject: overrideSubject.trim() || emailSubject,
        message: overrideMessage.trim() || emailMessage,
        scale: numericScale,
      });

      if (response && response.message === "Email sent successfully") {
        alertC("Email sent successfully!");
        onClose();
      } else {
        alertC("Failed to send email. Please try again.\n" + JSON.stringify(response, null, 2));
      }
    } catch (error) {
      console.error("Email send failed:", error);
      alertC("Failed to send email. Please try again.");
    } finally {
      if (pdfFixStyle && pdfFixStyle.parentNode) {
        pdfFixStyle.parentNode.removeChild(pdfFixStyle);
      }
      document.querySelectorAll('.specs-master-control').forEach(el => {
        el.style.display = el.dataset.originalDisplay || '';
        delete el.dataset.originalDisplay;
      });
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="modal-content">
        <h3>Send PDF by Email</h3>

        <input
          type="email"
          placeholder="Recipient Email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          className="input-email"
        />

        <div style={{ marginTop: "6px", textAlign: "right" }}>
          <button
            type="button"
            onClick={() => setShowMore(v => !v)}
            style={{ fontSize: "0.8em", background: "none", border: "none", color: "#555", cursor: "pointer", padding: "2px 4px" }}
          >
            {showMore ? "<< Less" : ">> More"}
          </button>
        </div>

        {showMore && (
          <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "6px" }}>
            <input
              type="text"
              placeholder="Recipient Name (optional — used for <<recipient>> tag)"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              className="input-email"
            />
            <input
              type="text"
              placeholder="Email Subject (optional — overrides default)"
              value={overrideSubject}
              onChange={(e) => setOverrideSubject(e.target.value)}
              className="input-email"
            />
            <textarea
              placeholder="Email Body (optional — overrides default)"
              value={overrideMessage}
              onChange={(e) => setOverrideMessage(e.target.value)}
              className="input-email"
              rows={5}
              style={{ resize: "vertical", fontFamily: "inherit" }}
            />
          </div>
        )}

        <div style={{ marginTop: "10px", display: "none" }}>
          <label>
            PDF Scale (1.0 to 5.0):
            <input
              type="number"
              step="0.1"
              min="1"
              max="5"
              value={scale}
              onChange={(e) => setScale(e.target.value)}
              style={{ marginLeft: "8px", width: "60px" }}
            />
          </label>
          <div style={{ fontSize: "0.8em", color: "#666", marginTop: "4px" }}>
            Higher values = better quality, larger file. Try 1.5–2.5.
          </div>
        </div>

        <div style={{ marginTop: "16px" }}>
          <button onClick={handleSubmit} disabled={isSending}>
            {isSending ? "Sending..." : "Send"}
          </button>
          <button onClick={onClose} style={{ marginLeft: "10px" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};


export default EmailDialog;
